import type { CameraState, Size } from '../types';
import type { SurfaceMesh } from '../geometry/surfaceMesh';
import type { ColorScale } from './colorScale';
import type { LightingModel } from './lighting';
import type { DrapedGrid } from './drapedGrid';
import type { SurfacePaint } from './surfacePaint';
import { rasterizeMeshWithDepth } from './rasterizeMesh';
import { applyOutlinesFromDepth, type OutlineInk } from './outlines';

export type PhongFillArgs = {
  context: CanvasRenderingContext2D;
  mesh: SurfaceMesh;
  camera: CameraState;
  viewport: Size;
  colorScale: ColorScale;
  lighting: LightingModel;
  shading: number;
  scale: number;
  opacity: number;
  /** Axis grid lines drawn onto the surface; null draws none. */
  drapedGrid: DrapedGrid | null;
  /** Colors the surface in place of the ramp; null colors it by height. */
  paint: SurfacePaint | null;
  /** Inks the silhouette and occluding contours over the surface; null draws none. */
  outline: OutlineInk | null;
  /** Bands each light's diffuse term is rounded to, for cel shading; 0 leaves it smooth. */
  celSteps: number;
};

/**
 * Reused across paints rather than allocated per call: on the software path this runs every frame
 * of an orbit, and a fresh canvas plus its backing store each time is megabytes of garbage per
 * second on the path that is already the slow one. Held in a mutable holder so the binding itself
 * stays `const`.
 *
 * Shared by every chart on the page, which is safe for a reason worth stating rather than leaving
 * to be re-derived: `renderPhongFill` is synchronous from `rasterizeMesh` through `putImageData`
 * to `drawImage`, so a second chart's call cannot begin until the first has finished reading. Two
 * charts of different sizes do resize it in turn and get no reuse at all - a lost saving, not a
 * hazard. Introduce an `await` anywhere between the rasterise and the composite and that stops
 * being true.
 */
const scratch: { canvas: HTMLCanvasElement | null } = { canvas: null };

/**
 * The scratch canvas at the requested size, resized only when the size actually changes.
 *
 * Safe to share: every caller writes the whole buffer with `putImageData` and composites it before
 * returning, so nothing can observe another frame's pixels.
 */
const getScratchContext = (
  width: number,
  height: number
): CanvasRenderingContext2D | null => {
  scratch.canvas ??= document.createElement('canvas');
  if (scratch.canvas.width !== width || scratch.canvas.height !== height) {
    scratch.canvas.width = width;
    scratch.canvas.height = height;
  }
  return scratch.canvas.getContext('2d');
};

/**
 * Composites the software-rasterised surface onto the chart's canvas.
 *
 * Through an offscreen canvas and `drawImage` rather than `putImageData`. `putImageData` ignores
 * the context transform and writes raw device pixels, so it cannot be scaled - and this buffer is
 * deliberately not at device resolution, since per-pixel shading in JavaScript is the expensive
 * part and rendering an orbit at half scale is what keeps it responsive. `drawImage` respects the
 * transform and lets the browser do the upscale, which it does on the GPU.
 */
export const renderPhongFill = ({
  context,
  mesh,
  camera,
  viewport,
  colorScale,
  lighting,
  shading,
  scale,
  opacity,
  drapedGrid,
  paint,
  outline,
  celSteps,
}: PhongFillArgs): void => {
  const rasterized = rasterizeMeshWithDepth({
    mesh,
    camera,
    viewport,
    colorScale,
    lighting,
    shading,
    scale,
    drapedGrid,
    paint,
    celSteps,
  });
  if (!rasterized) return;
  const { image } = rasterized;
  if (outline && outline.opacity > 0) {
    applyOutlinesFromDepth(image, rasterized, outline);
  }

  const offscreenContext = getScratchContext(image.width, image.height);
  if (!offscreenContext) return;
  offscreenContext.putImageData(image, 0, 0);

  context.save();
  context.globalAlpha = opacity;
  /* Smoothed on upscale: the buffer is a lower-resolution sampling of a continuous surface, so
     interpolating between its pixels is closer to the truth than replicating them as blocks. */
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    offscreenContext.canvas,
    0,
    0,
    viewport.width,
    viewport.height
  );
  context.restore();
};
